'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Car, MessageSquare, Plus, Globe, Sparkles, User,
  LogOut, LayoutDashboard, Moon, Sun, ChevronDown,
  Wrench, ShoppingBag, Boxes, Gavel, Gauge, Store,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCountry } from '@/context/CountryContext';
import { useTheme } from '@/context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { useBranding } from '@/context/BrandingContext';
import { useLanguage } from '@/context/LanguageContext';
import {
  clearStoredSession,
  getDashboardPathForRole,
  getVendorWorkspacePath,
  notifySessionChanged,
  readStoredSession,
} from '@/lib/auth/session';

function useAuthStatus() {
  const [user, setUser] = useState<{ full_name: string; role: string; email: string } | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const syncAuth = () => {
      const session = readStoredSession();
      setUser(session.isAuthenticated ? (session.user as any) : null);
      setReady(true);
    };
    syncAuth();
    window.addEventListener('storage', syncAuth);
    window.addEventListener('s360-auth-changed', syncAuth);
    return () => {
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('s360-auth-changed', syncAuth);
    };
  }, []);

  const logout = async () => {
    const refresh = localStorage.getItem('s360_refresh');
    const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    if (refresh) {
      try {
        await fetch(`${API}/api/auth/logout/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        });
      } catch { }
    }
    clearStoredSession();
    notifySessionChanged();
    setUser(null);
    window.location.href = '/';
  };

  return { user, ready, logout };
}

const NAV_MENUS = [
  {
    label: 'Car Market',
    href: '/buy',
    children: [
      { label: 'Buy a Car', href: '/buy', icon: Car },
      { label: 'Car Auctions', href: '/auctions', icon: Gavel },
      { label: 'Sell My Car', href: '/sell', icon: Plus },
    ],
  },
  {
    label: 'Public Market',
    href: '/marketplace',
    children: [
      { label: 'Marketplace', href: '/marketplace', icon: Store },
      { label: 'Spare Parts', href: '/parts', icon: Boxes },
    ],
  },
  {
    label: 'Maintenance',
    href: '/maintenance',
    children: [
      { label: 'Workshops', href: '/maintenance', icon: Wrench },
    ],
  },
  { label: 'Spare Parts', href: '/parts' },
  {
    label: 'Integrated Services',
    href: '/services',
    children: [
      { label: 'Services Hub', href: '/services', icon: ShoppingBag },
      { label: 'AI Diagnose', href: '/diagnose', icon: Sparkles },
      { label: 'AI Inspection', href: '/inspection', icon: Sparkles },
    ],
  },
  { label: 'Smart Inspection', href: '/diagnose' },
];

const HEADER_TRANS: Record<string, Record<'ar' | 'en', string>> = {
  'Car Market': { ar: 'سوق السيارات', en: 'Car Market' },
  'Buy a Car': { ar: 'شراء سيارة', en: 'Buy a Car' },
  'Car Auctions': { ar: 'مزادات السيارات', en: 'Car Auctions' },
  'Sell My Car': { ar: 'بيع سيارتي', en: 'Sell My Car' },
  'Public Market': { ar: 'السوق العام', en: 'Public Market' },
  'Marketplace': { ar: 'المتجر المفتوح', en: 'Marketplace' },
  'Spare Parts': { ar: 'قطع الغيار', en: 'Spare Parts' },
  'Maintenance': { ar: 'الصيانة والورش', en: 'Maintenance' },
  'Workshops': { ar: 'ورش الصيانة', en: 'Workshops' },
  'Integrated Services': { ar: 'الخدمات المتكاملة', en: 'Integrated Services' },
  'Services Hub': { ar: 'مركز الخدمات', en: 'Services Hub' },
  'AI Diagnose': { ar: 'التشخيص الذكي', en: 'AI Diagnose' },
  'AI Inspection': { ar: 'الفحص البصري', en: 'AI Inspection' },
  'Smart Inspection': { ar: 'الفحص الذكي', en: 'Smart Inspection' },
  'Post Ad': { ar: 'أعلن الآن', en: 'Post Ad' },
  'Login': { ar: 'دخول', en: 'Login' },
  'Dashboard': { ar: 'لوحة التحكم', en: 'Dashboard' },
  'Sign Out': { ar: 'خروج', en: 'Sign Out' },
  'Select Country': { ar: 'اختر البلد', en: 'Select Country' },
};

export default function DesktopHeader() {
  const { setSelectedCountryCode, selectedCountry, selectedCountryCode, countries } = useCountry();
  const { lang, toggleLanguage, t } = useLanguage();
  const pathname = usePathname();
  const { user, ready, logout } = useAuthStatus();
  const { isDark, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const initials = user?.full_name
    ?.split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('') || '?';

  const dashboardHref =
    user?.role === 'vendor'
      ? getVendorWorkspacePath(user as any)
      : getDashboardPathForRole(user?.role);

  return (
    <header
      className={cn(
        'hidden md:flex fixed top-0 left-0 right-0 z-50 h-16 items-center justify-between px-6 transition-all duration-300',
        scrolled
          ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-lg border-b border-slate-100/80 dark:border-slate-800/80'
          : 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-transparent'
      )}
    >
      {/* ── Logo ── */}
      <Link href="/" className="flex items-center gap-2.5 shrink-0 group">
        <div className="relative">
          <img
            src="/maknna_logo.png"
            alt="Maknna"
            className="h-9 w-9 rounded-xl object-cover shadow-md shadow-blue-200/50 group-hover:scale-105 transition-transform duration-200"
          />
        </div>
        <span className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
          Makn<span className="text-orange-500">na</span>
        </span>
      </Link>

      {/* ── Navigation ── */}
      <nav className="flex items-center gap-1 ml-6">
        {NAV_MENUS.map((item) => {
          const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
          const menuLabel = HEADER_TRANS[item.label]?.[lang as 'ar' | 'en'] || item.label;
          if (item.children) {
            return (
              <DropdownMenu key={item.href}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'nav-link flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-bold transition-colors duration-150',
                      isActive
                        ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40'
                        : 'text-slate-600 dark:text-slate-300 hover:text-blue-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    )}
                  >
                    {menuLabel}
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  sideOffset={8}
                  className="rounded-2xl p-2 shadow-2xl border border-slate-100/80 bg-white/95 backdrop-blur-xl min-w-[180px]"
                >
                  {item.children.map((child) => {
                    const childLabel = HEADER_TRANS[child.label]?.[lang as 'ar' | 'en'] || child.label;
                    return (
                      <DropdownMenuItem key={child.href} asChild>
                        <Link
                          href={child.href}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 hover:text-blue-700 hover:bg-blue-50 transition-colors cursor-pointer"
                        >
                          {child.icon && <child.icon className="h-4 w-4 text-blue-500" />}
                          {childLabel}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          return (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                'nav-link px-3 py-2 rounded-lg text-sm font-bold transition-colors duration-150',
                isActive
                  ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40'
                  : 'text-slate-600 dark:text-slate-300 hover:text-blue-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              )}
            >
              {menuLabel}
            </Link>
          );
        })}
      </nav>

      {/* ── Right side controls ── */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Language toggle */}
        <button
          onClick={toggleLanguage}
          className="h-9 px-3 rounded-lg text-xs font-black text-slate-600 dark:text-slate-300 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/40 border border-slate-200 dark:border-slate-700 transition-colors flex items-center gap-1.5"
        >
          <Globe className="h-3.5 w-3.5" />
          {lang === 'ar' ? 'EN' : 'AR'}
        </button>

        {/* Country */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-9 px-3 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/40 border border-slate-200 dark:border-slate-700 transition-colors hidden lg:flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5 text-blue-500" />
              {selectedCountry?.name_ar || selectedCountry?.name || (lang === 'ar' ? 'البلد' : 'Country')}
              <ChevronDown className="h-3 w-3 opacity-50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-2xl w-48 p-2 shadow-2xl border-none bg-white/95 backdrop-blur-xl">
            <div className="p-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">{HEADER_TRANS['Select Country']?.[lang as 'ar' | 'en'] || 'Select Country'}</div>
            {countries.map((c) => (
              <DropdownMenuItem
                key={c.code}
                onClick={() => setSelectedCountryCode(c.code)}
                className={cn(
                  'rounded-xl cursor-pointer font-bold text-sm py-2.5 transition-colors',
                  selectedCountryCode === c.code ? 'bg-blue-50 text-blue-700' : ''
                )}
              >
                {c.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Dark mode */}
        <button
          onClick={toggleTheme}
          className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-500 hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-950/30 border border-slate-200 dark:border-slate-700 transition-colors"
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Chat */}
        <Link href="/chat">
          <button className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 border border-slate-200 dark:border-slate-700 transition-colors">
            <MessageSquare className="h-4 w-4" />
          </button>
        </Link>

        {/* Post Ad CTA */}
        <Button asChild className="h-9 rounded-lg px-4 bg-orange-500 hover:bg-orange-600 text-white font-black text-sm shadow-md shadow-orange-200 btn-pulse ml-1">
          <Link href="/sell" className="flex items-center gap-1.5">
            <Plus className="h-4 w-4" />
            {HEADER_TRANS['Post Ad']?.[lang as 'ar' | 'en'] || 'Post Ad'}
          </Link>
        </Button>

        {/* Auth */}
        {ready && (
          <>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white font-black text-sm flex items-center justify-center shadow-md shadow-blue-200 hover:shadow-lg hover:scale-105 transition-all cursor-pointer">
                    {initials}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-2xl w-52 p-2 shadow-2xl border-none bg-white/95 backdrop-blur-xl">
                  <div className="px-3 py-2 mb-1">
                    <p className="font-black text-slate-800 text-sm truncate">{user.full_name}</p>
                    <p className="text-xs text-slate-400 truncate">{user.email?.includes('@public') ? user.role : user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="rounded-xl cursor-pointer font-bold text-sm py-2.5">
                    <Link href={dashboardHref} className="flex items-center gap-2">
                      <LayoutDashboard className="h-4 w-4 text-blue-500" />
                      {HEADER_TRANS['Dashboard']?.[lang as 'ar' | 'en'] || 'Dashboard'}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="rounded-xl cursor-pointer font-bold text-sm py-2.5 text-red-600 hover:bg-red-50 focus:bg-red-50 focus:text-red-600 flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    {HEADER_TRANS['Sign Out']?.[lang as 'ar' | 'en'] || 'Sign Out'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm" className="rounded-lg h-9 px-4 text-slate-600 hover:text-blue-700 hover:bg-blue-50 font-bold text-sm">
                  <Link href="/login" className="flex items-center gap-1.5">
                    <User className="h-4 w-4" />
                    {HEADER_TRANS['Login']?.[lang as 'ar' | 'en'] || 'Login'}
                  </Link>
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </header>
  );
}
